import { useState } from 'react';
import { motion } from 'framer-motion';
import { LuCheck, LuX } from 'react-icons/lu';
import { Card } from '../../../design-system/components';
import SectionHeading from '../components/SectionHeading';
import PricingToggle from '../components/PricingToggle';
import PricingCard from '../components/PricingCard';

const plans = {
  free: {
    name: 'Free',
    monthlyPrice: '$0',
    yearlyPrice: '$0',
    period: 'forever',
    description: 'Get started with the basics',
    features: [],
    cta: 'Get Started',
    ctaLink: '/register',
  },
  premium: {
    name: 'Premium',
    monthlyPrice: '$9.99',
    yearlyPrice: '$99.99',
    originalMonthlyPrice: '$9.99',
    period: '/mo',
    yearlyPeriod: '/yr',
    description: 'Full access to learn & practice',
    features: [],
    cta: 'Get Premium',
    ctaLink: '/register',
    highlighted: true,
  },
  elite: {
    name: 'Elite',
    monthlyPrice: '$99.99',
    yearlyPrice: '$999.99',
    originalMonthlyPrice: '$99.99',
    period: '/mo',
    yearlyPeriod: '/yr',
    description: 'For empathy professionals',
    features: [],
    cta: 'Go Elite',
    ctaLink: '/register',
  },
};

// Plan structure (current as of PR3 / 2026-05): the only plan-gated
// features are oral quiz questions and cohort hosting. Everything else
// (Echo Arena, Mediation, Empy, AI tools, support chat, on-demand
// empathy circles, 1-on-1 empathy) is open to all plans and gated by
// monthly credit allotment instead. The comparison table reflects
// that — credits are the differentiator, not feature locks.
const comparisonFeatures = [
  { name: 'Full NVC Course', free: true, premium: true, elite: true },
  { name: 'Quiz Questions (Text)', free: true, premium: true, elite: true },
  { name: 'AI Tutor (Empy)', free: true, premium: true, elite: true },
  { name: 'Giraffe Translator', free: true, premium: true, elite: true },
  { name: 'Needs Guesser', free: true, premium: true, elite: true },
  { name: 'Echo Arena & Mediation', free: true, premium: true, elite: true },
  { name: 'On-demand Empathy Circles', free: true, premium: true, elite: true },
  { name: '1-on-1 Empathy Sessions', free: true, premium: true, elite: true },
  { name: 'Support Chat', free: true, premium: true, elite: true },
  { name: 'Community Leaderboard', free: true, premium: true, elite: true },
  { name: 'Progress Tracking', free: true, premium: true, elite: true },
  { name: 'Oral Quiz Questions', free: false, premium: true, elite: true },
  { name: 'Host & Manage Cohorts', free: false, premium: true, elite: true },
];

const creditLimits = { free: '100', premium: '2,500', elite: '30,000' };

// Each mark is a named image (b-017): an aria-hidden icon read every plan
// cell as empty. A name, not text, so the page's crawled copy is unchanged.
function CheckIcon() {
  return (
    <LuCheck
      className="ee-landing-compare__yes"
      role="img"
      aria-label="Included"
      focusable="false"
    />
  );
}

function XIcon() {
  return (
    <LuX
      className="ee-landing-compare__no"
      role="img"
      aria-label="Not included"
      focusable="false"
    />
  );
}

export default function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="ee-landing-section ee-landing-section--warm">
      <div className="ee-landing-container ee-landing-container--mid">
        <SectionHeading
          title="Simple, Transparent Pricing"
          subtitle="Start free. Upgrade when you're ready."
        />

        <PricingToggle isYearly={isYearly} onToggle={() => setIsYearly(!isYearly)} />

        <div className="ee-landing-grid ee-landing-grid--3 ee-landing-plans">
          <PricingCard
            name={plans.free.name}
            price={plans.free.monthlyPrice}
            period={plans.free.period}
            description={plans.free.description}
            features={plans.free.features}
            cta={plans.free.cta}
            ctaLink={plans.free.ctaLink}
            index={0}
          />
          <PricingCard
            name={plans.premium.name}
            price={isYearly ? plans.premium.yearlyPrice : plans.premium.monthlyPrice}
            period={isYearly ? plans.premium.yearlyPeriod : plans.premium.period}
            description={plans.premium.description}
            features={plans.premium.features}
            cta={plans.premium.cta}
            ctaLink={plans.premium.ctaLink}
            highlighted={plans.premium.highlighted}
            originalPrice={isYearly ? '$119.88' : undefined}
            index={1}
          />
          <PricingCard
            name={plans.elite.name}
            price={isYearly ? plans.elite.yearlyPrice : plans.elite.monthlyPrice}
            period={isYearly ? plans.elite.yearlyPeriod : plans.elite.period}
            description={plans.elite.description}
            features={plans.elite.features}
            cta={plans.elite.cta}
            ctaLink={plans.elite.ctaLink}
            originalPrice={isYearly ? '$1,199.88' : undefined}
            index={2}
          />
        </div>

        <p className="ee-landing-plans-note">
          7-day free trial on all paid plans. No credit card required.
        </p>

        {/* Compare Plans Grid */}
        <motion.div
          className="ee-landing-compare"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="ee-landing-compare__title">Compare Plans</h3>
          <Card padding="none" className="ee-landing-compare__scroll">
            <table className="ee-landing-compare__table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th className="ee-landing-compare__featured">Premium</th>
                  <th>Elite</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature) => (
                  <tr key={feature.name}>
                    <td>{feature.name}</td>
                    <td>
                      <div className="ee-landing-compare__mark">
                        {feature.free ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                    <td>
                      <div className="ee-landing-compare__mark">
                        {feature.premium ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                    <td>
                      <div className="ee-landing-compare__mark">
                        {feature.elite ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Credits row — the actual difference between plans */}
                <tr className="ee-landing-compare__credits">
                  <td>Monthly credits</td>
                  <td>{creditLimits.free}</td>
                  <td className="ee-landing-compare__featured">{creditLimits.premium}</td>
                  <td>{creditLimits.elite}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
